/**
 * frontend/src/components/OpinionFlotante.ts
 * ============================================
 * El botón para dejar una opinión, encima del de reportar un problema (al lado,
 * en pantallas bajas o angostas).
 *
 * Tres aspectos con cinco caras cada uno y uno general al final. Nada es
 * obligatorio: alcanza con una cara o una línea. Son pocos a propósito,
 * porque un formulario largo no se completa.
 *
 * El comentario de cada aspecto está siempre a la vista, en una línea, y su
 * pregunta acompaña a la cara elegida: «¿Qué no funcionó?», «¿Qué le faltó?»
 * o «¿Qué le gustó?». Una pregunta concreta da comentarios más útiles que un
 * «Comentario» genérico.
 *
 * Después de la tercera conciliación completada, una invitación discreta lo
 * recuerda una sola vez: sin ella, la opinión depende de que el usuario se
 * acuerde del botón, y el que se acuerda suele ser el que tuvo un problema.
 */

import { ApiError, enviarOpinion } from "../api";
import { escapeHtml as _esc } from "../utils/texto";
import { icon, type IconName } from "../utils/icons";
import { prepararDialogo } from "../utils/ui";

/**
 * Los aspectos que se puntúan. Las claves son las de `src/comun/opiniones.py`
 * —con ellas se agrupan las respuestas en el servidor— y un test verifica que
 * coincidan.
 */
export const ASPECTOS = [
  {
    clave: "facilidad",
    titulo: "Facilidad de uso",
    pregunta: "¿Le resultó fácil cargar los archivos, configurar los perfiles y llegar al resultado?",
  },
  {
    clave: "precision",
    titulo: "Precisión del resultado",
    pregunta: "¿La conciliación coincidió con lo que usted esperaba?",
  },
  {
    clave: "claridad",
    titulo: "Claridad del reporte",
    pregunta: "¿Entendió el resumen y el reporte sin necesidad de ayuda?",
  },
] as const;

const CARAS: { puntaje: number; icono: IconName; etiqueta: string }[] = [
  { puntaje: 1, icono: "cara1", etiqueta: "Muy insatisfecho" },
  { puntaje: 2, icono: "cara2", etiqueta: "Insatisfecho" },
  { puntaje: 3, icono: "cara3", etiqueta: "Neutral" },
  { puntaje: 4, icono: "cara4", etiqueta: "Satisfecho" },
  { puntaje: 5, icono: "cara5", etiqueta: "Muy satisfecho" },
];

/** Lo que pregunta el comentario según la cara elegida. */
const PREGUNTA_POR_PUNTAJE: Record<number, string> = {
  1: "¿Qué no funcionó?",
  2: "¿Qué no funcionó?",
  3: "¿Qué le faltó?",
  4: "¿Qué le gustó?",
  5: "¿Qué le gustó?",
};

/** Conciliaciones completadas tras las cuales se invita a opinar. */
export const INVITAR_TRAS = 3;

const AYUDA = "Denos su opinión";

let boton: HTMLButtonElement | null = null;
let usuarioActivo = "";
let pantallaActual: () => string = () => "";

interface Seguimiento {
  completadas: number;
  invitado: boolean;
  enviada: boolean;
}

function claveSeguimiento(): string {
  return `conciliador_opinion:${usuarioActivo}`;
}

function leerSeguimiento(): Seguimiento {
  try {
    const guardado = JSON.parse(localStorage.getItem(claveSeguimiento()) ?? "null");
    if (guardado) return guardado as Seguimiento;
  } catch {
    // Sin almacenamiento disponible: se invita como la primera vez.
  }
  return { completadas: 0, invitado: false, enviada: false };
}

function guardarSeguimiento(s: Seguimiento): void {
  try {
    localStorage.setItem(claveSeguimiento(), JSON.stringify(s));
  } catch {
    // Es una comodidad: si no se puede guardar, a lo sumo se invita de nuevo.
  }
}

/**
 * Coloca el botón. Idempotente.
 *
 * @param pantalla se consulta al enviar: la pantalla cambia mientras el botón
 *   sigue ahí.
 */
export function montarBotonDeOpinion(usuario: string, pantalla: () => string): void {
  usuarioActivo = usuario;
  pantallaActual = pantalla;
  if (boton) return;

  boton = document.createElement("button");
  boton.type = "button";
  boton.className = "opinion-flotante";
  boton.id = "btn-opinion";
  boton.innerHTML = icon("cara4");
  boton.setAttribute("aria-label", AYUDA);
  boton.setAttribute("data-tooltip", AYUDA);
  boton.addEventListener("click", () => {
    quitarInvitacion();
    abrirFormulario();
  });
  document.body.appendChild(boton);
}

/** Saca el botón y la invitación. Se usa al cerrar sesión. */
export function desmontarBotonDeOpinion(): void {
  quitarInvitacion();
  boton?.remove();
  boton = null;
}

/**
 * Cuenta una conciliación completada y, al llegar a la tercera, invita a
 * opinar. Una sola vez, y nunca a quien ya opinó.
 */
export function registrarConciliacionCompletada(): void {
  const s = leerSeguimiento();
  s.completadas += 1;
  const invitar = s.completadas >= INVITAR_TRAS && !s.invitado && !s.enviada;
  if (invitar) s.invitado = true;
  guardarSeguimiento(s);
  if (invitar) mostrarInvitacion();
}

function quitarInvitacion(): void {
  document.getElementById("opinion-invitacion")?.remove();
}

function mostrarInvitacion(): void {
  if (!boton || document.getElementById("opinion-invitacion")) return;

  const globo = document.createElement("div");
  globo.className = "opinion-invitacion";
  globo.id = "opinion-invitacion";
  globo.setAttribute("role", "status");
  globo.innerHTML = `
    <p class="opinion-invitacion__texto">¿Cómo le está resultando ConciliA? Su opinión nos ayuda a mejorar.</p>
    <div class="opinion-invitacion__acciones">
      <button class="btn btn--ghost btn--sm" type="button" data-accion="despues">Ahora no</button>
      <button class="btn btn--primary btn--sm" type="button" data-accion="opinar">Dar mi opinión</button>
    </div>`;
  globo.querySelector('[data-accion="despues"]')!.addEventListener("click", quitarInvitacion);
  globo.querySelector('[data-accion="opinar"]')!.addEventListener("click", () => {
    quitarInvitacion();
    abrirFormulario();
  });
  document.body.appendChild(globo);
}

function abrirFormulario(): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "opinion-titulo");

  const aspectos = ASPECTOS.map((a) => `
    <div class="opinion-aspecto" role="group" aria-labelledby="opinion-${a.clave}" data-aspecto="${a.clave}">
      <div class="opinion-aspecto__texto">
        <h3 class="opinion-aspecto__titulo" id="opinion-${a.clave}">${_esc(a.titulo)}</h3>
        <p class="opinion-aspecto__pregunta">${_esc(a.pregunta)}</p>
      </div>
      <div class="opinion-caras" role="radiogroup" aria-label="${_esc(a.titulo)}">
        ${CARAS.map((c) => `
          <button class="opinion-cara opinion-cara--${c.puntaje}" type="button" role="radio" aria-checked="false"
                  data-puntaje="${c.puntaje}" aria-label="${c.puntaje} de 5: ${c.etiqueta}"
                  data-tooltip="${c.etiqueta}">${icon(c.icono)}</button>`).join("")}
      </div>
      <textarea class="opinion-aspecto__comentario" rows="1" maxlength="1000"
                aria-label="Comentario sobre ${_esc(a.titulo.toLowerCase())}"
                placeholder="Comentario (opcional)"></textarea>
    </div>`).join("");

  overlay.innerHTML = `
    <div class="modal-panel modal-panel--opinion">
      <div class="modal-header">
        <h2 class="modal-header__title" id="opinion-titulo">${AYUDA}</h2>
        <button class="modal-close" type="button" aria-label="Cerrar">${icon("close")}</button>
      </div>

      <form class="modal-body opinion-form" id="opinion-form" novalidate>
        ${aspectos}
        <label class="opinion-general">
          <span class="opinion-general__label">¿Qué mejoraría primero?</span>
          <textarea id="opinion-general" rows="1" maxlength="2000" placeholder="Opcional"></textarea>
        </label>
        <p class="reporte-estado" id="opinion-estado" role="status" aria-live="polite" hidden></p>
      </form>

      <div class="modal-footer">
        <button class="btn btn--ghost" type="button" id="opinion-cancelar">Cancelar</button>
        <button class="btn btn--primary" type="submit" form="opinion-form" id="opinion-enviar" disabled>
          <span>Enviar opinión</span>
        </button>
      </div>
    </div>`;

  document.body.appendChild(overlay);

  const form    = overlay.querySelector<HTMLFormElement>("#opinion-form")!;
  const general = overlay.querySelector<HTMLTextAreaElement>("#opinion-general")!;
  const estado  = overlay.querySelector<HTMLParagraphElement>("#opinion-estado")!;
  const enviar  = overlay.querySelector<HTMLButtonElement>("#opinion-enviar")!;
  const puntajes: Record<string, number | null> = {};

  const cerrar = (): void => overlay.remove();
  prepararDialogo(overlay, cerrar);
  overlay.querySelector(".modal-close")!.addEventListener("click", cerrar);
  overlay.querySelector("#opinion-cancelar")!.addEventListener("click", cerrar);

  const comentarios = (): Record<string, string> => Object.fromEntries(
    Array.from(overlay.querySelectorAll<HTMLElement>(".opinion-aspecto")).map((f) => [
      f.dataset.aspecto!,
      f.querySelector<HTMLTextAreaElement>("textarea")!.value.trim(),
    ]),
  );

  // Enviar se habilita apenas haya algo: un formulario vacío no aporta nada.
  const revisar = (): void => {
    const hayAlgo = Object.values(puntajes).some(Boolean)
      || Object.values(comentarios()).some(Boolean)
      || general.value.trim() !== "";
    enviar.disabled = !hayAlgo;
    if (hayAlgo) enviar.removeAttribute("data-tooltip");
    else enviar.setAttribute("data-tooltip", "Elija una cara o escriba un comentario.");
  };

  overlay.querySelectorAll<HTMLElement>(".opinion-aspecto").forEach((fieldset) => {
    const clave = fieldset.dataset.aspecto!;
    const caras = Array.from(fieldset.querySelectorAll<HTMLButtonElement>(".opinion-cara"));
    const texto = fieldset.querySelector<HTMLTextAreaElement>("textarea")!;

    caras.forEach((cara) => cara.addEventListener("click", () => {
      const puntaje = Number(cara.dataset.puntaje);
      // Tocar la cara elegida la desmarca: nada es obligatorio, tampoco
      // quedarse con una cara elegida por error.
      puntajes[clave] = puntajes[clave] === puntaje ? null : puntaje;
      for (const c of caras) {
        c.setAttribute("aria-checked", String(Number(c.dataset.puntaje) === puntajes[clave]));
      }
      // La pregunta del comentario acompaña a la cara elegida.
      texto.placeholder = puntajes[clave] ? PREGUNTA_POR_PUNTAJE[puntajes[clave]!] : "Comentario (opcional)";
      revisar();
    }));

    // El comentario crece al escribir: empieza en una línea.
    texto.addEventListener("input", () => {
      texto.style.height = "auto";
      texto.style.height = `${texto.scrollHeight}px`;
      revisar();
    });
  });
  general.addEventListener("input", () => {
    general.style.height = "auto";
    general.style.height = `${general.scrollHeight}px`;
    revisar();
  });
  revisar();

  const mostrar = (mensaje: string, tono: "error" | "exito"): void => {
    estado.className = `reporte-estado reporte-estado--${tono}`;
    estado.innerHTML = `${icon(tono === "error" ? "alert" : "check")} <span>${_esc(mensaje)}</span>`;
    estado.hidden = false;
  };

  form.addEventListener("submit", async (evento) => {
    evento.preventDefault();
    if (enviar.disabled) return;

    enviar.disabled = true;
    enviar.querySelector("span")!.textContent = "Enviando…";
    estado.hidden = true;

    const textos = comentarios();
    try {
      await enviarOpinion({
        calificaciones: Object.fromEntries(ASPECTOS.map((a) => [
          a.clave,
          { puntaje: puntajes[a.clave] ?? null, comentario: textos[a.clave] ?? "" },
        ])),
        comentario_general: general.value.trim(),
        pantalla: pantallaActual(),
      });
      guardarSeguimiento({ ...leerSeguimiento(), enviada: true });
      mostrar("¡Gracias! Su opinión llegó al equipo.", "exito");
      form.querySelectorAll<HTMLButtonElement | HTMLTextAreaElement>("button, textarea")
        .forEach((c) => { c.disabled = true; });
      enviar.querySelector("span")!.textContent = "Enviada";
      setTimeout(cerrar, 2500);
    } catch (err) {
      mostrar(err instanceof ApiError ? err.message : "No se pudo enviar su opinión.", "error");
      enviar.disabled = false;
      enviar.querySelector("span")!.textContent = "Enviar opinión";
    }
  });
}
